// Centralizes every import from the canonical data layer (src/data/source +
// src/data/generated), which lives 4 directories up from here at the
// heartopia-vite project root — see src/data/README.md for how that layer is
// built (npm run data:rebuild from the heartopia-vite root, NOT from this app).
//
// This file is the ONLY place in the app that should reach across that
// relative path. Everything else imports from "@/lib/data" instead.
//
// No database, no Prisma, no Supabase, no Redis — everything below is a
// plain TypeScript literal produced ahead of time by the data pipeline.

import {
  ITEMS_BY_ID,
  ITEM_IDS_BY_KIND,
  ITEM_IDS_BY_EVENT_KEY,
  ALIAS_TO_CANONICAL_ID,
  resolveItem,
} from "../../../../src/data/generated/indexes";
import { CANONICAL_ITEMS } from "../../../../src/data/generated/items";
import { CANONICAL_SEARCH_INDEX, type CanonicalSearchEntry } from "../../../../src/data/generated/search-index";
import { OBTAIN_METHODS, OBTAIN_METHODS_BY_ITEM_ID } from "../../../../src/data/generated/obtain-methods";
import {
  SHOPS_BY_ID,
  NPCS_BY_ID,
  LOCATIONS_BY_ID,
  SHOP_LISTINGS_BY_SHOP_ID,
  SHOPS_BY_LOCATION_ID,
} from "../../../../src/data/generated/entity-indexes";
import { CANONICAL_LOCATIONS, CANONICAL_NPCS, CANONICAL_SHOPS } from "../../../../src/data/generated/entities";
import itemAvailableLocalesJson from "../../../../src/data/generated/translations/available-locales";
import npcResearchOverridesJson from "../../../../src/data/source/npc-research-overrides.json";
import recipesJson from "../../../../src/data/source/recipes.json";
import recipeIngredientsJson from "../../../../src/data/source/recipe-ingredients.json";
import recipeStarValuesJson from "../../../../src/data/source/recipe-star-values.json";
import eventsJson from "../../../../src/data/source/events.json";
import weatherJson from "../../../../src/data/source/weather.json";
import petsJson from "../../../../src/data/source/pets.json";
import petTraitsJson from "../../../../src/data/source/pet-traits.json";
import achievementsJson from "../../../../src/data/source/achievements.json";
import giftCodesJson from "../../../../src/data/source/gift-codes.json";
import dailyTasksJson from "../../../../src/data/source/daily-tasks.json";
import currenciesJson from "../../../../src/data/source/currencies.json";
import masteryJson from "../../../../src/data/source/mastery.json";
import type {
  CanonicalItem,
  ItemKind,
  ObtainMethod,
  RecipeDef,
  RecipeIngredientRef,
  RecipeStarValues,
  LocationDef,
  NpcDef,
  ShopDef,
  ShopListingRef,
  EventDef,
  WeatherDef,
  PetDef,
  PetTraitDef,
  AchievementDef,
  GiftCodeDef,
  DailyTaskDef,
  CurrencyDef,
  MasteryDef,
  MasteryHobby,
} from "../../../../src/data/schema/types";
import type { NpcResearchRecord } from "../../../../src/data/schema/npc-research";
import type {
  NpcDetailFact,
  NpcDetailHobbyRelationship,
  NpcDetailSection,
  NpcDetailShopListing,
  NpcDetailViewModel,
} from "../../../../src/data/schema/npc-detail-view";
import type {
  WikiDetailViewModel,
  WikiEntityType,
  DetailFact,
  DetailBadge,
  DetailSection,
  DetailRelation,
  DetailObtainMethod,
  DetailShopListing,
  DetailItemPreview,
} from "../../../../src/data/schema/wiki-detail-view";
import { defaultLocale, locales, type Locale } from "@/lib/i18n/config";

const RECIPES = recipesJson as RecipeDef[];
const RECIPE_INGREDIENTS = recipeIngredientsJson as RecipeIngredientRef[];
const RECIPE_STAR_VALUES = recipeStarValuesJson as RecipeStarValues[];
const RECIPE_STAR_VALUES_BY_RECIPE_ID = new Map(RECIPE_STAR_VALUES.map((v) => [v.recipeId, v]));
const NPC_RESEARCH_OVERRIDES = npcResearchOverridesJson as NpcResearchRecord[];
const RECIPE_BY_OUTPUT_ITEM_ID = new Map(RECIPES.map((r) => [r.outputItemId, r]));
const NPC_RESEARCH_BY_ID = new Map(NPC_RESEARCH_OVERRIDES.map((record) => [record.npcId, record]));

export const CURRENCIES = currenciesJson as CurrencyDef[];
const CURRENCY_BY_ID = new Map(CURRENCIES.map((c) => [c.id, c]));
/** One entry per accepted spelling — shop-listings.json uses SCREAMING_SNAKE codes, not ids. */
const CURRENCY_BY_CODE = new Map(CURRENCIES.flatMap((c) => c.codes.map((code) => [code, c] as const)));

export function getCurrency(id: string): CurrencyDef | undefined {
  return CURRENCY_BY_ID.get(id);
}

/** Accepts either a `codes` entry or the id itself, so callers do not have to know which they hold. */
export function getCurrencyByCode(code: string): CurrencyDef | undefined {
  return CURRENCY_BY_CODE.get(code) ?? CURRENCY_BY_ID.get(code);
}

export type { CanonicalItem, ItemKind, ObtainMethod, CanonicalSearchEntry };
export type {
  WikiDetailViewModel,
  WikiEntityType,
  DetailFact,
  DetailBadge,
  DetailSection,
  DetailRelation,
  DetailObtainMethod,
  DetailShopListing,
  DetailItemPreview,
};
export type {
  NpcDetailFact,
  NpcDetailHobbyRelationship,
  NpcDetailSection,
  NpcDetailShopListing,
  NpcDetailViewModel,
  NpcResearchRecord,
};
export { CANONICAL_ITEMS, CANONICAL_SEARCH_INDEX, NPC_RESEARCH_OVERRIDES };

export interface LocalizedText {
  text: string;
  isFallback: boolean;
  sourceLocale: Locale;
}

export interface LocalizedCanonicalItem extends CanonicalItem {
  displayName: string;
  isNameFallback: boolean;
  nameSourceLocale: Locale;
}

/**
 * Official localized map-location names, imported from the game client.
 *
 * Keyed by both id and lowercased English name on purpose: half the call sites of
 * `LocalizedLocationName` only have the display string (habitat text, wild-animal profiles) and
 * never had a location id to pass.
 *
 * 29 of 36 locations are covered. The rest keep the suggest-a-translation affordance — see
 * `scripts/datamine/import_location_names.py` for why they were not paired by guesswork.
 */
const LOCATION_TRANSLATIONS = new Map<string, Map<Locale, string>>();

function registerLocationTranslations(locale: Locale, entries: Record<string, string>): void {
  for (const [locationId, name] of Object.entries(entries)) {
    const location = CANONICAL_LOCATIONS.find((l) => l.id === locationId);
    for (const key of [locationId, location?.name.toLowerCase()].filter(Boolean) as string[]) {
      const byLocale = LOCATION_TRANSLATIONS.get(key) ?? new Map<Locale, string>();
      byLocale.set(locale, name);
      LOCATION_TRANSLATIONS.set(key, byLocale);
    }
  }
}

/**
 * Localized one-line location descriptions — the text under the `<h1>` on `/locations/:id` and on
 * every card of `/locations`.
 *
 * Keyed by id only, unlike LOCATION_TRANSLATIONS above: a description is only ever rendered for a
 * location the caller already resolved to a record, so there is no display-string call site to
 * accommodate. All 36 locations are covered in all five non-English locales.
 */
const LOCATION_DESCRIPTIONS = new Map<string, Map<Locale, string>>();

function registerLocationDescriptions(locale: Locale, entries: Record<string, string>): void {
  for (const [locationId, description] of Object.entries(entries)) {
    const byLocale = LOCATION_DESCRIPTIONS.get(locationId) ?? new Map<Locale, string>();
    byLocale.set(locale, description);
    LOCATION_DESCRIPTIONS.set(locationId, byLocale);
  }
}

/**
 * Item name translations, one Map<itemId, Map<locale, name>> filled in lazily per locale (see
 * `ensureLocaleTranslations` below) instead of the flat 4,891-row translations.json that used to
 * be imported here wholesale — that put every locale's names in the chunk dist/index.html loads
 * on every page (530 KB as of 2026-08-15, es/id/pt-BR/th all at once). English isn't a locale
 * that gets loaded here at all: it's just CanonicalItem.name.
 */
const ITEM_TRANSLATIONS = new Map<string, Map<Locale, string>>();

function registerItemTranslations(locale: Locale, entries: Record<string, string>): void {
  for (const [itemId, name] of Object.entries(entries)) {
    const byLocale = ITEM_TRANSLATIONS.get(itemId) ?? new Map<Locale, string>();
    byLocale.set(locale, name);
    ITEM_TRANSLATIONS.set(itemId, byLocale);
  }
}

/**
 * One dynamic-import entry per non-English locale, written out explicitly rather than built from
 * a template literal (`import(`./translations/${locale}.ts`)`) — a literal specifier per locale
 * is what makes Vite emit one predictable chunk per locale; a template glob works too but would
 * silently widen to match any stray file dropped in that folder.
 */
const ITEM_TRANSLATION_LOADERS: Partial<Record<Locale, () => Promise<{ default: Record<string, string> }>>> = {
  es: () => import("../../../../src/data/generated/translations/es"),
  id: () => import("../../../../src/data/generated/translations/id"),
  "pt-BR": () => import("../../../../src/data/generated/translations/pt-BR"),
  th: () => import("../../../../src/data/generated/translations/th"),
};

const LOCATION_TRANSLATION_LOADERS: Partial<Record<Locale, () => Promise<{ default: Record<string, string> }>>> = {
  es: () => import("../../../../src/data/generated/location-translations/es"),
  id: () => import("../../../../src/data/generated/location-translations/id"),
  "pt-BR": () => import("../../../../src/data/generated/location-translations/pt-BR"),
  th: () => import("../../../../src/data/generated/location-translations/th"),
};

const LOCATION_DESCRIPTION_LOADERS: Partial<Record<Locale, () => Promise<{ default: Record<string, string> }>>> = {
  de: () => import("../../../../src/data/generated/location-descriptions/de"),
  es: () => import("../../../../src/data/generated/location-descriptions/es"),
  id: () => import("../../../../src/data/generated/location-descriptions/id"),
  "pt-BR": () => import("../../../../src/data/generated/location-descriptions/pt-BR"),
  th: () => import("../../../../src/data/generated/location-descriptions/th"),
};

const loadedItemLocales = new Set<Locale>([defaultLocale]);
const loadedLocationLocales = new Set<Locale>([defaultLocale]);
const loadedLocationDescriptionLocales = new Set<Locale>([defaultLocale]);

/**
 * Loads one locale's item + location names and registers them, idempotent per locale. Call this
 * before the first render that needs `locale`:
 * - `main.tsx` awaits it (for the URL's own locale) before `hydrateRoot`, so the first client
 *   render already has the same names the prerendered HTML does — loading it afterward would
 *   mean the first render shows English/fallback text where the server HTML shows the real name,
 *   which is exactly the React #418 hydration mismatch this file's callers work hard to avoid
 *   elsewhere (see usePersistentState's doc comment for the general pattern).
 * - `LocaleContext.tsx`'s `LocaleProvider` calls it when `locale` changes, for the in-app
 *   language switcher (`LanguageModal`) — that's a plain client-side React Router navigation, not
 *   a fresh hydration, so there is no mismatch risk there; it just needs a re-render once the
 *   chunk lands, which the provider's own state update supplies.
 */
export async function ensureLocaleTranslations(locale: Locale): Promise<void> {
  if (locale === defaultLocale) return;
  const tasks: Promise<void>[] = [];
  if (!loadedItemLocales.has(locale)) {
    loadedItemLocales.add(locale);
    const load = ITEM_TRANSLATION_LOADERS[locale];
    if (load) tasks.push(load().then((mod) => registerItemTranslations(locale, mod.default)));
  }
  if (!loadedLocationLocales.has(locale)) {
    loadedLocationLocales.add(locale);
    const load = LOCATION_TRANSLATION_LOADERS[locale];
    if (load) tasks.push(load().then((mod) => registerLocationTranslations(locale, mod.default)));
  }
  if (!loadedLocationDescriptionLocales.has(locale)) {
    loadedLocationDescriptionLocales.add(locale);
    const load = LOCATION_DESCRIPTION_LOADERS[locale];
    if (load) tasks.push(load().then((mod) => registerLocationDescriptions(locale, mod.default)));
  }
  await Promise.all(tasks);
}

/**
 * SSR-only. `scripts/generate-static-meta.ts` renders every route in every locale from a single
 * Node process (see `entry-server.tsx`'s non-lazy route tree), so it needs every locale in memory
 * before the first `render()` call rather than loading one locale at a time like the browser
 * does. Cheap here — this runs once in Node at build time, never in a browser.
 */
export async function ensureAllLocaleTranslations(): Promise<void> {
  await Promise.all(locales.filter((l) => l !== defaultLocale).map(ensureLocaleTranslations));
}

/** Accepts a location id or its English name; returns null when we have no official name. */
export function getLocationTranslation(idOrName: string, locale: Locale): string | null {
  if (locale === defaultLocale) return null;
  return (
    LOCATION_TRANSLATIONS.get(idOrName)?.get(locale) ??
    LOCATION_TRANSLATIONS.get(idOrName.toLowerCase())?.get(locale) ??
    null
  );
}

/** Localized description for a location id, or null on English (where `LocationDef.description`
 *  is itself the authored text) and for a locale with no row. */
export function getLocationDescription(locationId: string, locale: Locale): string | null {
  if (locale === defaultLocale) return null;
  return LOCATION_DESCRIPTIONS.get(locationId)?.get(locale) ?? null;
}

export function getItemTranslation(itemId: string, locale: Locale): string | null {
  if (locale === defaultLocale) return ITEMS_BY_ID[itemId]?.name ?? null;
  return ITEM_TRANSLATIONS.get(itemId)?.get(locale) ?? null;
}

const ITEM_AVAILABLE_LOCALES = itemAvailableLocalesJson as Record<string, Locale[]>;

/**
 * Non-English locales this item has a translated name in — for the hreflang cluster
 * (lib/seo/alternate-locales.ts#localesForItem). Deliberately does NOT go through
 * `ITEM_TRANSLATIONS`/`getItemTranslation`: those only hold whichever locale's names have been
 * loaded so far (see `ensureLocaleTranslations` above), so deriving hreflang from them would be
 * correct only for the current locale and wrong everywhere else, from the very first render. This
 * reads a small pre-baked itemId -> locale-codes index instead (no name strings), safe to load
 * unconditionally.
 */
export function getAvailableLocalesForItem(itemId: string): Locale[] {
  return ITEM_AVAILABLE_LOCALES[itemId] ?? [];
}

export function getLocalizedItemName(itemOrId: CanonicalItem | string, locale: Locale): LocalizedText {
  const item = typeof itemOrId === "string" ? getItem(itemOrId) : itemOrId;
  if (!item) {
    return {
      text: typeof itemOrId === "string" ? itemOrId : "",
      isFallback: true,
      sourceLocale: defaultLocale,
    };
  }

  const translation = getItemTranslation(item.id, locale);
  if (translation) {
    return {
      text: translation,
      isFallback: false,
      sourceLocale: locale,
    };
  }

  return {
    text: item.name,
    isFallback: locale !== defaultLocale,
    sourceLocale: defaultLocale,
  };
}

export function localizeItem(item: CanonicalItem, locale: Locale): LocalizedCanonicalItem {
  const localizedName = getLocalizedItemName(item, locale);
  return {
    ...item,
    displayName: localizedName.text,
    isNameFallback: localizedName.isFallback,
    nameSourceLocale: localizedName.sourceLocale,
  };
}

/** All 457+ canonical items, unfiltered. */
export function getAllItems(): CanonicalItem[] {
  return CANONICAL_ITEMS;
}

export function getAllLocalizedItems(locale: Locale): LocalizedCanonicalItem[] {
  return CANONICAL_ITEMS.map((item) => localizeItem(item, locale));
}

/** Looks up an item by id, resolving through aliases.json first. Returns undefined if unknown. */
export function getItem(id: string): CanonicalItem | undefined {
  return resolveItem(id);
}

export function getLocalizedItem(id: string, locale: Locale): LocalizedCanonicalItem | undefined {
  const item = getItem(id);
  return item ? localizeItem(item, locale) : undefined;
}

/** Seed ids whose produce item id doesn't follow the "-seed(s)" stripping convention. */
const SEED_PRODUCE_ID_OVERRIDES: Record<string, string> = {
  "potato-seed": "potatoes",
};

/**
 * The crop/flower a seed grows into, resolved by naming convention (strip a
 * "-seed"/"-seeds" suffix) with a small override table for irregular ids.
 * Returns undefined for non-seed items or when no matching produce item exists.
 */
export function getSeedProduceItem(seed: CanonicalItem): CanonicalItem | undefined {
  if (seed.kind !== "seed") return undefined;
  const produceId = SEED_PRODUCE_ID_OVERRIDES[seed.id] ?? seed.id.replace(/-seeds?$/, "");
  const candidate = getItem(produceId);
  return candidate && (candidate.kind === "crop" || candidate.kind === "flower") ? candidate : undefined;
}

/** Crop/flower ids whose seed item id doesn't follow the "-seed(s)" suffix convention. */
const CROP_SEED_ID_OVERRIDES: Record<string, string> = {
  potatoes: "potato-seed",
};

/**
 * The seed item that grows into this crop/flower — the inverse of getSeedProduceItem, resolved
 * the same way (naming convention + a small override table). Returns undefined when there's no
 * matching seed item in the catalog yet (a real data gap, e.g. "tea" and "white-radish" don't
 * have one) rather than guessing.
 */
export function getCropSeedItem(crop: CanonicalItem): CanonicalItem | undefined {
  if (crop.kind !== "crop" && crop.kind !== "flower") return undefined;
  const overrideId = CROP_SEED_ID_OVERRIDES[crop.id];
  if (overrideId) return getItem(overrideId);
  return getItem(`${crop.id}-seed`) ?? getItem(`${crop.id}-seeds`);
}

/**
 * Every color-variant item for a flower species (e.g. "daisy" -> Green Daisy,
 * Pink Daisy, Red Daisy, ...), sorted by display name. See
 * docs/FLOWER_VARIANT_MODEL.md for how these are derived. Empty for species
 * with no matched variant assets, and for non-flower/non-species ids.
 */
export function getFlowerVariants(speciesId: string): CanonicalItem[] {
  return CANONICAL_ITEMS.filter((item) => item.kind === "flower" && item.flowerSpecies === speciesId).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/**
 * Kind-agnostic version of the above. The colour-variant model started out flower-only, but
 * flower *seeds* have the same shape (2026-08-05): `<species>-seeds` is the group record and
 * `<colour>-<species>-seed` are its variants. Joins on `variantOf` rather than `flowerSpecies`
 * because that field is an item id and so cannot collide across kinds.
 */
export function getVariantsOf(parentId: string): CanonicalItem[] {
  return CANONICAL_ITEMS.filter((item) => item.variantOf === parentId).sort((a, b) => a.name.localeCompare(b.name));
}

export function getLocalizedVariantsOf(parentId: string, locale: Locale): LocalizedCanonicalItem[] {
  return getVariantsOf(parentId).map((item) => localizeItem(item, locale));
}

/** Same as getFlowerVariants, localized — use this in UI so color-variant names respect the current locale instead of always showing English. */
export function getLocalizedFlowerVariants(speciesId: string, locale: Locale): LocalizedCanonicalItem[] {
  return getFlowerVariants(speciesId).map((item) => localizeItem(item, locale));
}

/** All items of a given kind (fish, dish, insect, ...). */
export function getItemsByKind(kind: ItemKind): CanonicalItem[] {
  return (ITEM_IDS_BY_KIND[kind] ?? []).map((id) => ITEMS_BY_ID[id]).filter(Boolean);
}

export function getLocalizedItemsByKind(kind: ItemKind, locale: Locale): LocalizedCanonicalItem[] {
  return getItemsByKind(kind).map((item) => localizeItem(item, locale));
}

/** Every kind that has at least one item, with its count — used by the Home page. */
export function getCountsByKind(): { kind: string; count: number }[] {
  return Object.entries(ITEM_IDS_BY_KIND)
    .map(([kind, ids]) => ({ kind, count: ids.length }))
    .sort((a, b) => b.count - a.count);
}

/** Obtain methods for an item (fishing spot, farming info, etc.) — empty for dishes/crafted/shop-only items. */
export function getObtainMethods(itemId: string): ObtainMethod[] {
  return OBTAIN_METHODS_BY_ITEM_ID[itemId] ?? [];
}

/**
 * Fish/insects/birds tagged with a given weather condition (e.g. "SUNNY", "RAINY", "RAINBOW").
 * Only some weather values are actually tagged in the source data — callers should handle an
 * empty result gracefully rather than assuming every weather key has creatures attached.
 */
export function getItemsForWeather(weatherKey: string, locale: Locale): LocalizedCanonicalItem[] {
  const kinds: ItemKind[] = ["fish", "insect", "bird"];
  return kinds
    .flatMap((kind) => getItemsByKind(kind))
    .filter((item) => getObtainMethods(item.id).some((m) => m.weather?.includes(weatherKey)))
    .map((item) => localizeItem(item, locale))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * What a player can actually collect at a location — the one thing a location page has to say
 * that nothing else on the site says, and the reason `/locations/:id` was a map and two lists.
 *
 * `ObtainMethod.locationIds` is the explicit link and covers 176 of the 567 rows. The exact
 * habitat-name fallback is for the handful the client wrote as free text and never gave an id:
 * it matches on a *normalised* whole string, never a substring, because "Fishing Village" is a
 * substring of "Fishing Village Square" and would hand one location the other's creatures.
 * Normalising is what makes the location "Spirit Oak Pine Forest" meet the habitat
 * "Spirit-Oak Pine Forest" — a hyphen apart, six creatures.
 */
export interface LocationCollectible {
  item: LocalizedCanonicalItem;
  method: ObtainMethod["method"];
  requiredLevel: number | null;
  timeOfDay: string[];
  weather: string[];
  shadowSize: string | null;
  isEventOnly: boolean;
}

function normaliseHabitatKey(value: string): string {
  return value.toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

export function getCollectiblesAtLocation(locationId: string, locale: Locale): LocationCollectible[] {
  const location = LOCATIONS_BY_ID[locationId];
  if (!location) return [];
  const habitatKey = normaliseHabitatKey(location.name);

  const seen = new Set<string>();
  const collectibles: LocationCollectible[] = [];

  for (const method of OBTAIN_METHODS) {
    const explicit = method.locationIds.includes(locationId);
    const byHabitat = !explicit && !!method.habitat && normaliseHabitatKey(method.habitat) === habitatKey;
    if (!explicit && !byHabitat) continue;

    const item = ITEMS_BY_ID[method.itemId];
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);

    collectibles.push({
      item: localizeItem(item, locale),
      method: method.method,
      requiredLevel: method.requiredLevel ?? null,
      timeOfDay: method.timeOfDay ?? [],
      weather: method.weather ?? [],
      shadowSize: method.shadowSize ?? null,
      isEventOnly: method.isEventOnly,
    });
  }

  return collectibles.sort((a, b) => a.item.displayName.localeCompare(b.item.displayName));
}

/** Known aliases pointing at this item (old ids, typos, spelling variants). */
export function getAliasesFor(item: CanonicalItem): string[] {
  return item.aliases;
}

export { ALIAS_TO_CANONICAL_ID, ITEM_IDS_BY_EVENT_KEY };

export interface ShopSummary {
  id: string;
  name: string;
  /** There is no `/shops/:id` route, so a shop is only reachable through its keeper's page. */
  npcId?: string | null;
  npcName?: string | null;
  totalListings?: number;
}

export interface LocationSummary {
  id?: string;
  name: string;
}

/** Shops that sell this item, with NPC name + total listing count (for the detail-page "Where to buy" section). */
export function getShopsForItem(itemId: string): ShopSummary[] {
  const shopIds = Object.keys(SHOP_LISTINGS_BY_SHOP_ID).filter((shopId) =>
    SHOP_LISTINGS_BY_SHOP_ID[shopId].some((listing) => listing.itemId === itemId),
  );
  return shopIds.map((shopId) => {
    const shop = SHOPS_BY_ID[shopId];
    const npc = shop?.npcId ? NPCS_BY_ID[shop.npcId] : undefined;
    return {
      id: shopId,
      name: shop?.name ?? shopId,
      npcName: npc?.name ?? null,
      totalListings: SHOP_LISTINGS_BY_SHOP_ID[shopId].length,
    };
  });
}

/**
 * Per-listing shop details for an item, with price/currency attached (unlike getShopsForItem,
 * which aggregates to one row per shop with a shop-wide listing count, not a price). Added for
 * the WikiDetailViewModel adapter layer — no existing call site changed.
 */
export function getShopListingsForItem(itemId: string): DetailShopListing[] {
  const results: DetailShopListing[] = [];
  for (const shopId of Object.keys(SHOP_LISTINGS_BY_SHOP_ID)) {
    const listings = SHOP_LISTINGS_BY_SHOP_ID[shopId].filter((listing) => listing.itemId === itemId);
    if (listings.length === 0) continue;
    const shop = SHOPS_BY_ID[shopId];
    const npc = shop?.npcId ? NPCS_BY_ID[shop.npcId] : undefined;
    const location = shop?.locationId ? LOCATIONS_BY_ID[shop.locationId] : undefined;
    for (const listing of listings) {
      results.push({
        shopId,
        shopName: shop?.name ?? shopId,
        npcId: npc?.id ?? null,
        npcName: npc?.name ?? null,
        locationId: location?.id ?? null,
        locationName: location?.name ?? null,
        price: listing.price,
        currency: listing.currency,
      });
    }
  }
  return results;
}

/** Best-effort location summaries for an item — from its obtain method's locationIds, falling back to raw habitat text. */
export function getLocationSummariesForItem(itemId: string): LocationSummary[] {
  const methods = getObtainMethods(itemId);
  const ids = Array.from(new Set(methods.flatMap((m) => m.locationIds)));
  if (ids.length > 0) {
    return ids.map((id) => ({ id, name: LOCATIONS_BY_ID[id]?.name ?? id }));
  }
  const habitat = methods.find((m) => m.habitat)?.habitat;
  return habitat ? [{ name: habitat }] : [];
}

export type { RecipeDef, RecipeIngredientRef };

/** The recipe that produces this dish item (recipeId === the dish's canonical id via outputItemId). */
export function getRecipeForDish(dishItemId: string): RecipeDef | undefined {
  return RECIPE_BY_OUTPUT_ITEM_ID.get(dishItemId);
}

/** Ingredients required for a recipe — each ref has either itemId or ingredientTag, never both. */
export function getRecipeIngredients(recipeId: string): RecipeIngredientRef[] {
  return RECIPE_INGREDIENTS.filter((ing) => ing.recipeId === recipeId);
}

/**
 * The dish items whose recipe uses this item as a fixed ingredient (reverse of
 * getRecipeIngredients) — for a "Used in Recipes" section on crop/fish/ingredient detail
 * pages. Deliberately ignores ingredientTag rows (e.g. "Any Fruit") since those are curated
 * per-recipe substitute slots, not a confirmed use of this specific item — see
 * RecipeIngredientRef.mixOptionItemIds for where a curated match does exist.
 */
export function getRecipesUsingIngredient(itemId: string): CanonicalItem[] {
  const recipeIds = new Set(
    RECIPE_INGREDIENTS.filter((ing) => ing.itemId === itemId || ing.mixOptionItemIds?.includes(itemId)).map(
      (ing) => ing.recipeId,
    ),
  );
  return RECIPES.filter((r) => recipeIds.has(r.id))
    .map((r) => getItem(r.outputItemId))
    .filter((item): item is CanonicalItem => !!item);
}

export type { MasteryDef, MasteryHobby };

const MASTERY = masteryJson as MasteryDef[];
const MASTERY_BY_ITEM_ID = new Map(MASTERY.map((m) => [m.itemId, m]));

/**
 * Per-item Hobby Mastery thresholds (see mastery.json). Undefined for roughly a quarter of
 * fish/insects/birds/crops and for every other kind — a genuine data gap, not an error, so
 * callers should render the "we don't know this yet" affordance rather than hiding the section.
 * Note this is unrelated to a dish's Cooking Mastery, which lives on `RecipeDef`.
 */
export function getMastery(itemId: string): MasteryDef | undefined {
  return MASTERY_BY_ITEM_ID.get(itemId);
}

/** Every item with mastery data for a hobby, easiest first — backs the /mastery-style rankings. */
export function getMasteryByHobby(hobby: MasteryHobby): MasteryDef[] {
  return MASTERY.filter((m) => m.hobby === hobby).sort((a, b) => a.novice - b.novice);
}

export type { RecipeStarValues };

/** Real, scraped per-star Energy/Market values for a recipe (see recipe-star-values.json).
 * Returns undefined when we don't have confirmed values — callers should fall back to the
 * standard multiplier table (RECIPE_STAR_MULTIPLIERS in lib/calc.ts) in that case. */
export function getRecipeStarValues(recipeId: string): RecipeStarValues | undefined {
  return RECIPE_STAR_VALUES_BY_RECIPE_ID.get(recipeId);
}

/* --- Feature entities promoted into the data layer (2026-08-04) -------------
 * Raw, un-hydrated records. Each feature's own `data.ts` re-exports these with its `iconKey`
 * turned back into a lucide component (see lib/feature-icons.ts) and keeps the type names its
 * pages already use. Import these directly only if you do not need the icon. */
export const EVENTS_DATA = eventsJson as EventDef[];
export const WEATHER_DATA = weatherJson as WeatherDef[];
export const PETS_DATA = petsJson as PetDef[];
export const PET_TRAITS_DATA = petTraitsJson as PetTraitDef[];
export const ACHIEVEMENTS_DATA = achievementsJson as AchievementDef[];
export const GIFT_CODES_DATA = giftCodesJson as GiftCodeDef[];
export const DAILY_TASKS_DATA = dailyTasksJson as DailyTaskDef[];

export type { EventDef, WeatherDef, PetDef, PetTraitDef, AchievementDef, GiftCodeDef, DailyTaskDef };
export type {
  EventKind,
  EventStatus,
  EventRequirementDef,
  EventLocationDef,
  EventCostDef,
  WeatherId,
  PetSpecies,
  PetGroup,
  PetVariantDef,
  CodeStatus,
  CodeItemRewardDef,
  CodeRewardsDef,
  DailyTaskCategory,
} from "../../../../src/data/schema/types";

export type { LocationDef, NpcDef };

/** All 46 canonical locations. */
export function getAllLocations(): LocationDef[] {
  return CANONICAL_LOCATIONS;
}

export function getLocation(id: string): LocationDef | undefined {
  return LOCATIONS_BY_ID[id];
}

/** Shops physically located at this location (via SHOPS_BY_LOCATION_ID). */
export function getShopsAtLocation(locationId: string): ShopSummary[] {
  const shopIds = SHOPS_BY_LOCATION_ID[locationId] ?? [];
  return shopIds.map((shopId) => {
    const shop = SHOPS_BY_ID[shopId];
    const npc = shop?.npcId ? NPCS_BY_ID[shop.npcId] : undefined;
    return {
      id: shopId,
      name: shop?.name ?? shopId,
      npcId: npc?.id ?? null,
      npcName: npc?.name ?? null,
      totalListings: SHOP_LISTINGS_BY_SHOP_ID[shopId]?.length ?? 0,
    };
  });
}

/** NPCs whose locationText mentions this location's name (best-effort text match). */
export function getNpcsAtLocation(locationId: string): NpcDef[] {
  const location = LOCATIONS_BY_ID[locationId];
  if (!location) return [];
  return CANONICAL_NPCS.filter((npc) => npc.locationText?.toLowerCase() === location.name.toLowerCase());
}

/** All 24 canonical NPCs. */
export function getAllNpcs(): NpcDef[] {
  return CANONICAL_NPCS;
}

export function getNpc(id: string): NpcDef | undefined {
  return NPCS_BY_ID[id];
}

export function getNpcResearch(npcId: string): NpcResearchRecord | undefined {
  return NPC_RESEARCH_BY_ID.get(npcId);
}

function addNpcDetailFact(
  facts: NpcDetailFact[],
  fact: Omit<NpcDetailFact, "value" | "sourceRefs"> & { value?: string | null; sourceRefs?: string[] },
): void {
  if (!fact.value) return;
  facts.push({ ...fact, value: fact.value, sourceRefs: fact.sourceRefs ?? [] });
}

function getCanonicalShopsForNpc(npcId: string): ShopDef[] {
  return CANONICAL_SHOPS.filter((shop) => shop.npcId === npcId);
}

function getNpcShopListings(shops: ShopDef[]): NpcDetailShopListing[] {
  return shops.flatMap((shop) =>
    (SHOP_LISTINGS_BY_SHOP_ID[shop.id] ?? []).map((listing: ShopListingRef) => ({
      listing,
      item: ITEMS_BY_ID[listing.itemId] ?? null,
    })),
  );
}

function getNpcDetailFacts(npc: NpcDef, research: NpcResearchRecord | null): NpcDetailFact[] {
  const facts: NpcDetailFact[] = [];
  addNpcDetailFact(facts, {
    key: "role",
    label: "Role",
    value: npc.role?.replace(/_/g, " ") ?? null,
    confidence: "confirmed",
    source: "canonical",
  });
  addNpcDetailFact(facts, {
    key: "type",
    label: "NPC Type",
    value: npc.type.replace(/_/g, " "),
    confidence: "confirmed",
    source: "canonical",
  });
  addNpcDetailFact(facts, {
    key: "locationText",
    label: "Canonical Location Text",
    value: npc.locationText ?? null,
    confidence: "confirmed",
    source: "canonical",
  });
  addNpcDetailFact(facts, {
    key: "teachesHobby",
    label: "Canonical Taught Hobby",
    value: npc.teachesHobby?.replace(/_/g, " ") ?? null,
    confidence: "confirmed",
    source: "canonical",
  });

  if (!research) return facts;

  for (const label of research.identity?.externalRoleLabels ?? []) {
    addNpcDetailFact(facts, {
      key: `research-role-${label}`,
      label: "Research Role Label",
      value: label,
      confidence: "likely",
      source: "research",
    });
  }
  for (const label of research.identity?.externalNpcTypeLabels ?? []) {
    addNpcDetailFact(facts, {
      key: `research-type-${label}`,
      label: "Research NPC Type Label",
      value: label,
      confidence: "likely",
      source: "research",
    });
  }
  addNpcDetailFact(facts, {
    key: "research-stock-model",
    label: "Research Stock Model",
    value: research.shop?.stockModel ?? null,
    confidence: "likely",
    source: "research",
  });
  addNpcDetailFact(facts, {
    key: "research-listing-completeness",
    label: "Research Listing Completeness",
    value: research.shop?.listingCompleteness ?? null,
    confidence: "likely",
    source: "research",
  });
  return facts;
}

function collectSectionSourceRefs(entries: unknown[]): string[] {
  return Array.from(
    new Set(
      entries.flatMap((entry) => {
        if (entry && typeof entry === "object" && "sourceRefs" in entry && Array.isArray(entry.sourceRefs)) {
          return entry.sourceRefs.filter((sourceRef): sourceRef is string => typeof sourceRef === "string");
        }
        return [];
      }),
    ),
  );
}

function getNpcDetailSections(research: NpcResearchRecord | null): NpcDetailSection[] {
  if (!research) return [];
  const sections: NpcDetailSection[] = [];
  const identityNotes = research.identity?.notes ?? [];
  if (identityNotes.length > 0) {
    sections.push({
      key: "identity-notes",
      title: "Identity Research Notes",
      kind: "notes",
      entries: identityNotes,
      sourceRefs: [],
    });
  }
  const locationLabels = research.locations?.externalLocationLabels ?? [];
  if (locationLabels.length > 0) {
    sections.push({
      key: "location-claims",
      title: "Location Claims",
      kind: "claims",
      entries: locationLabels,
      sourceRefs: collectSectionSourceRefs(locationLabels),
    });
  }
  const stockNotes = research.shop?.stockNotes ?? [];
  if (stockNotes.length > 0) {
    sections.push({
      key: "shop-stock-notes",
      title: "Shop Stock Notes",
      kind: "notes",
      entries: stockNotes,
      sourceRefs: [],
    });
  }
  const gifts = research.gifts?.favoriteGifts ?? [];
  if (gifts.length > 0 || research.gifts?.birthday) {
    sections.push({
      key: "gifts-preferences",
      title: "Gifts and Preferences",
      kind: "gifts",
      entries: [...gifts, ...(research.gifts?.birthday ? [research.gifts.birthday] : [])],
      sourceRefs: collectSectionSourceRefs(gifts),
    });
  }
  const relatedEntities = research.relatedEntities ?? [];
  if (relatedEntities.length > 0) {
    sections.push({
      key: "related-entities",
      title: "Related Entities",
      kind: "entities",
      entries: relatedEntities,
      sourceRefs: collectSectionSourceRefs(relatedEntities),
    });
  }
  if (research.openQuestions.length > 0) {
    sections.push({
      key: "open-questions",
      title: "Open Questions",
      kind: "questions",
      entries: research.openQuestions,
      sourceRefs: [],
    });
  }
  return sections;
}

export function getNpcDetailViewModel(id: string): NpcDetailViewModel | undefined {
  const npc = getNpc(id);
  if (!npc) return undefined;

  const research = getNpcResearch(id) ?? null;
  const canonicalShops = getCanonicalShopsForNpc(id);
  const researchLocalShopId = research?.shop?.localShopId ?? null;
  const resolvedResearchShop = researchLocalShopId ? (SHOPS_BY_ID[researchLocalShopId] ?? null) : null;
  const relatedEntities = research?.relatedEntities ?? [];
  const shopRelationship =
    canonicalShops.length > 0 || research?.shop
      ? {
          canonicalShops,
          researchLocalShopId,
          resolvedResearchShop,
          externalShopNames: research?.shop?.externalShopNames ?? [],
          recommendedDisplayName: research?.shop?.recommendedDisplayName ?? null,
          stockModel: research?.shop?.stockModel ?? null,
          listingCompleteness: research?.shop?.listingCompleteness ?? null,
          currencies: research?.shop?.currencies ?? [],
          stockNotes: research?.shop?.stockNotes ?? [],
        }
      : null;
  const recommendedLocationId = research?.locations?.recommendedLocationId ?? null;

  return {
    identity: {
      entityType: "npc",
      id: npc.id,
      slug: npc.id,
    },
    display: {
      name: npc.name,
      localizedName: research?.identity?.externalName,
      role: npc.role ?? null,
      npcType: npc.type,
      portrait: null,
    },
    facts: getNpcDetailFacts(npc, research),
    relationships: {
      location:
        npc.locationText || research?.locations
          ? {
              canonicalText: npc.locationText ?? research?.locations?.localLocationText ?? null,
              recommendedLocationId,
              recommendedLocationText: research?.locations?.recommendedLocationText ?? null,
              resolvedRecommendedLocation: recommendedLocationId
                ? (LOCATIONS_BY_ID[recommendedLocationId] ?? null)
                : null,
              externalLabels: research?.locations?.externalLocationLabels ?? [],
              conflictStatus: research?.locations?.conflictStatus ?? null,
              notes: research?.locations?.notes ?? [],
            }
          : null,
      shop: shopRelationship,
      shopItems: getNpcShopListings(canonicalShops),
      taughtHobby:
        npc.teachesHobby || research?.hobby
          ? {
              canonicalHobby: npc.teachesHobby ?? null,
              research: research?.hobby ?? null,
            }
          : null,
      quests: [],
      relatedItems: relatedEntities.filter((entity) => entity.entityType === "item"),
      relatedGuides: relatedEntities.filter((entity) => entity.entityType === "guide"),
      relatedEntities,
    },
    sections: getNpcDetailSections(research),
    sourceRefs: research?.sourceRefs ?? [],
    qualityFlags: research?.qualityFlags ?? [],
    openQuestions: research?.openQuestions ?? [],
    canonical: npc,
    research,
  };
}

/** The shop this NPC runs, if they're a shop NPC. */
export function getShopForNpc(npcId: string): ShopSummary | undefined {
  const shop = CANONICAL_SHOPS.find((s) => s.npcId === npcId);
  if (!shop) return undefined;
  return {
    id: shop.id,
    name: shop.name,
    npcName: NPCS_BY_ID[npcId]?.name ?? null,
    totalListings: SHOP_LISTINGS_BY_SHOP_ID[shop.id]?.length ?? 0,
  };
}

export interface ShopListingDetail {
  id: string;
  itemId: string;
  itemKind: ItemKind | null;
  itemName: string;
  itemNameFallback: boolean;
  itemImage: string | null;
  itemImageStatus: string;
  price: number;
  currency: string;
}

export interface ShopWithListings {
  id: string;
  name: string;
  npc: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  listings: ShopListingDetail[];
}

/** All shops with resolved NPC, location, and full item listing details — for the /shops page. */
export function getAllShopsWithListings(locale: Locale = defaultLocale): ShopWithListings[] {
  return CANONICAL_SHOPS.map((shop) => {
    const npc = shop.npcId ? NPCS_BY_ID[shop.npcId] : undefined;
    const location = shop.locationId ? LOCATIONS_BY_ID[shop.locationId] : undefined;
    const listings = (SHOP_LISTINGS_BY_SHOP_ID[shop.id] ?? []).map((listing) => {
      const item = ITEMS_BY_ID[listing.itemId];
      const localizedName = item ? getLocalizedItemName(item, locale) : { text: listing.itemId, isFallback: true };
      return {
        id: listing.id,
        itemId: listing.itemId,
        itemKind: item?.kind ?? null,
        itemName: localizedName.text,
        itemNameFallback: localizedName.isFallback,
        itemImage: item?.image ?? null,
        itemImageStatus: item?.imageStatus ?? "missing",
        price: listing.price,
        currency: listing.currency,
      };
    });
    return {
      id: shop.id,
      name: shop.name,
      npc: npc ? { id: npc.id, name: npc.name } : null,
      location: location ? { id: location.id, name: location.name } : null,
      listings,
    };
  });
}

/**
 * Whether an item's detail page is withheld from the index for having no stat worth indexing.
 *
 * Re-exported rather than imported directly by the route, so this module stays the single place
 * that reaches the data layer by relative path. The Node build scripts import the same module, so
 * the sitemap, the prerendered head and the hydrated head cannot disagree about which pages these
 * are — the failure mode alternate-locales.ts already warns about.
 */
export { isThinItemPage, THIN_ITEM_IDS, isEmptyItemKind } from "../../../../src/data/thin-items";
